import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { observer } from 'mobx-react'
import { useParams } from 'react-router-dom'
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/20/solid'
import _ from 'lodash'

// Components
import { Button } from '../../components/Button'
import { DataTable } from '../../components/DataTable'
import { PageContainer } from '../../components/PageContainer'
import { StateContainer } from '../../components/StateContainer'
import { TextInput } from '../../components/TextInput'
import BenchmarkPanel from './BenchmarkPanel'

// Services
import { getClient } from '../../services/clients.service'
import {
  deleteClientBenchmark,
  getBenchmarkDataCount,
  getClientBenchmarks,
} from '../../services/benchmarks.service'

// Stores
import { ClientDashboardStoreContext } from '../../stores/ClientDashboardStore'

// Utils & Hooks
import { toast } from '../../utils/helpers'
import { usePagination, useSorting } from '../../hooks/DataTableManagement'

const NEW_BENCHMARK_ID = '__new__'

/**
 *
 * Benchmarks
 *
 */
const Benchmarks = observer(() => {
  const { clientId } = useParams()
  const { setClient } = useContext(ClientDashboardStoreContext)

  const { pagination, setTotalRecords } = usePagination(10)
  const { perPage, currentPage } = pagination

  const { sorting } = useSorting('name')
  const { sortedColumn } = sorting

  const [loading, setLoading] = useState(true)
  const [loadingBenchmarks, setLoadingBenchmarks] = useState(false)
  const [loadingDelete, setLoadingDelete] = useState(false)
  const [error, setError] = useState(null)
  const [benchmarks, setBenchmarks] = useState([])
  const [editingBenchmarkId, setEditingBenchmarkId] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [search, setSearch] = useState('')
  const [benchmarkDataCounts, setBenchmarkDataCounts] = useState({})

  const BASE_URL = `/clients/${clientId}/benchmarks/?`
  const searchInputRef = useRef()

  const handleSuccess = (m) => toast(m, 'success')
  const handleErrors = (m) => toast(m, 'error')

  const buildListUrl = () =>
    `${BASE_URL}&order_by=${sortedColumn}&limit=${perPage}&page=${currentPage}${
      search ? `&q=${search}` : ''
    }`

  const getUpdatedBenchmarks = async (url) => {
    const response = await getClientBenchmarks(url, setError, setLoadingBenchmarks)

    if (response) {
      setTotalRecords(response.count)
      setBenchmarks(response.results)

      const countPromises = response.results.map((benchmark) =>
        getBenchmarkDataCount(
          clientId,
          benchmark.id,
          () => {},
          () => {},
        ),
      )
      const countResults = await Promise.all(countPromises)
      const counts = {}
      response.results.forEach((benchmark, idx) => {
        counts[benchmark.id] = countResults[idx]
      })
      setBenchmarkDataCounts(counts)
    }
  }

  useEffect(() => {
    if (clientId) {
      const getUpdatedClient = async () => {
        const updatedClient = await getClient(clientId, setError, setLoading)

        if (updatedClient) {
          setClient(updatedClient)
        }
      }

      getUpdatedClient()
    }
  }, [clientId])

  useEffect(() => {
    if (clientId) {
      getUpdatedBenchmarks(buildListUrl())
    }
  }, [perPage, clientId, sortedColumn, currentPage, search])

  const searchBenchmarks = useCallback(_.debounce(setSearch, 500), [])

  const editingBenchmark = useMemo(() => {
    if (!editingBenchmarkId) return null
    if (editingBenchmarkId === NEW_BENCHMARK_ID) return null
    return benchmarks.find((b) => b.id === editingBenchmarkId) || null
  }, [editingBenchmarkId, benchmarks])

  const panelOpen = editingBenchmarkId !== null

  const closePanel = () => {
    setEditingBenchmarkId(null)
    getUpdatedBenchmarks(buildListUrl())
  }

  return (
    <PageContainer>
      <StateContainer error={error} loading={loading}>
        <div className="flex size-full flex-col overflow-y-auto bg-background">
          <div className="space-y-6">
            <div className="flex flex-col gap-2">
              <div className="flex w-full flex-col justify-between sm:flex-row sm:items-center">
                <h2 className="text-xl font-semibold leading-6 text-gray-900">Benchmarks</h2>

                <Button
                  label="Add Benchmark"
                  onClick={() => setEditingBenchmarkId(NEW_BENCHMARK_ID)}
                />
              </div>

              <TextInput
                className="w-full rounded-full py-2.5 pl-10 pr-4 placeholder:font-normal placeholder:text-gray-600 md:w-[450px]"
                icon={
                  <MagnifyingGlassIcon className="ml-2 h-5 text-gray-dark" aria-hidden="true" />
                }
                id="search"
                endIcon={
                  searchTerm ? (
                    <Button
                      type="button"
                      onClick={() => {
                        setSearchTerm('')
                        searchBenchmarks(null)
                        searchInputRef.current.value = ''
                      }}
                      icon={<XMarkIcon className="mr-2 h-5 text-gray-dark" aria-hidden="true" />}
                      ariaLabel="Clear search"
                      iconOnly
                    />
                  ) : null
                }
                name="search"
                onChange={(e) => {
                  setSearchTerm(e.target.value)
                  searchBenchmarks(e.target.value)
                }}
                placeholder="Looking for something?"
                ref={searchInputRef}
                value={searchTerm}
              />
            </div>

            <DataTable
              data={benchmarks}
              columns={[
                {
                  field: 'name',
                  header: 'Benchmark Name',
                  style: { minWidth: '200px' },
                  sortable: true,
                },
                {
                  field: 'component',
                  header: 'Component Name',
                  style: { minWidth: '200px' },
                  sortable: true,
                },
                {
                  field: 'displays',
                  header: 'Report Names',
                  style: { minWidth: '140px' },
                  body: (row) => (row.displays || []).length,
                },
                {
                  header: '',
                  frozen: true,
                  alignFrozen: 'right',
                  style: { minWidth: '80px' },
                  body: (row) => (
                    <Button
                      background="bg-blue"
                      label="Edit"
                      onClick={() => setEditingBenchmarkId(row.id)}
                    />
                  ),
                },
              ]}
              loadingDelete={loadingDelete}
              hoverEffect={false}
              loading={loadingBenchmarks}
              pagination={pagination}
              sorting={sorting}
              onRowDeleteConfirm={(row) => {
                deleteClientBenchmark(clientId, row.id, handleErrors, setLoadingDelete, () => {
                  handleSuccess('Benchmark deleted.')
                  getUpdatedBenchmarks(buildListUrl())
                })
              }}
              areRowsDeleteable
              rowDeleteConfirm={(row) => {
                const benchmarkCount = benchmarkDataCounts[row.id] || 0
                const benchmarkMessage =
                  benchmarkCount > 0
                    ? ` This will delete ${benchmarkCount} benchmark data record${
                      benchmarkCount === 1 ? '' : 's'
                    } associated with this benchmark.`
                    : ''

                return {
                  title: `Delete Benchmark`,
                  type: 'warning',
                  message: (
                    <div className="flex flex-col">
                      <span className="">Are you sure you want to delete the benchmark:</span>
                      <span className="font-semibold">
                        {row.name} ({row.component})
                      </span>
                      {benchmarkMessage && (
                        <div className="mt-2 rounded border-l-4 border-orange-400 bg-orange-50 p-2">
                          <span className="text-orange-800">{benchmarkMessage}</span>
                        </div>
                      )}
                    </div>
                  ),
                }
              }}
            />
          </div>
        </div>
      </StateContainer>

      <BenchmarkPanel
        benchmark={editingBenchmark}
        clientId={clientId}
        closePanel={closePanel}
        onChanged={() => getUpdatedBenchmarks(buildListUrl())}
        panelOpen={panelOpen}
      />
    </PageContainer>
  )
})

export default Benchmarks
